/**
 * Sending an approver to sign a stage that needs a passkey: mint the approval
 * challenge, then open the signing page with the assignment being answered and
 * where to come back to. The one implementation behind the approval page and
 * both Tasks surfaces (a row's actions and the task drawer).
 */
import { useToast } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

import { useStartApprovalSignature } from "state/members/stepUp";
import {
  failureMessage,
  START_SIGNING_FAILED_MESSAGE,
} from "state/members/stepUpFailure";

import { approvalSigningState, stepUpPath } from "./navigation";

export type ApprovalToSign = {
  requestId: string;
  /** The approval assignment the approver came from. */
  assigneeId: string;
  /** Where to go once signed. */
  returnTo: string;
  /** The approver's note, recorded with the decision once signed. */
  comments?: string;
};

/** Opens the signing page; says why and returns false if it cannot. */
export const useApprovalSigning = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const start = useStartApprovalSignature();

  const sign = async ({
    requestId,
    assigneeId,
    returnTo,
    comments,
  }: ApprovalToSign): Promise<boolean> => {
    try {
      const challenge = await start.mutateAsync(requestId);
      navigate(stepUpPath(challenge.id), {
        state: approvalSigningState(assigneeId, returnTo, comments),
      });
      return true;
    } catch (error) {
      toast({
        status: "error",
        variant: "subtle",
        title: "We could not start signing",
        description: failureMessage(error, START_SIGNING_FAILED_MESSAGE),
      });
      return false;
    }
  };

  return { sign, isStarting: start.isLoading };
};
